import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hooks } from 'farmx-redux-core';
import {
  Form, Button, Row, Col
} from 'antd';
import { BlockSelect, RanchSelect, EntitySelect } from 'farmx-web-ui';
import ReusableSelect from "./ReusableSelect";
import './nodeConfig.less';
import NodeTypeList from './NodeTypeList';
/**
 * How to use:
 * const extraFilters = [
    { key: 'currentFwVersion', name: 'Current Version', element: <SomeReactElement />},
    { key: 'lastFwVersion', name: 'Last Version', rules: [{ required: true }],
      element: <SomeReactElement />},
    ]
 * render:
 * <OptionalFilters renderItems={['ranch', 'block', 'apply']} extraFilters={extraFilters}/>
 */

interface OptionalFiltersProps {
  onChange?: (data: any) => void;
  onApply?: (data: any) => void;
  extraFilters?: object | {};
  renderItems?: string[];
  statusDropdownOptions? : string[];
  entityId?: string | undefined;
  onEntityChange?: (entityId: string) => void;
  loading: boolean,
}

/* eslint-disable no-template-curly-in-string */
const validateMessages = {
  required: '${label} is required!',
};

const { useEntities } = hooks;

const OptionalFilters: React.FC<OptionalFiltersProps> = (props: any) => {
  const {
    onChange, onApply, extraFilters, renderItems, statusDropdownOptions, entityId, onEntityChange, loading
  } = props;
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);

  const prepareFormItem = (obj: any) => (
    <Form.Item
      name={obj?.key}
      label={t(obj?.name)}
      rules={obj?.rules}
      className="font-weight-bold margin-bottom-0"
    >
      {obj?.element}
    </Form.Item>
  );

  const renderStatus = () => prepareFormItem({
    key: 'status',
    name: 'Status',
    element: 
      <ReusableSelect 
        options={statusDropdownOptions} 
        onChange={(d) => setSelectedStatus(d)}
        value={selectedStatus}
        className='fixed-width-100'
        placeholder='please select'
      />
  });

  const renderEntity = () => prepareFormItem({
    key: 'entity',
    name: 'Select Org',
    rules: [{required: true}],
    element:  (
      <EntitySelect 
        onChange={(d) => onEntityChange && onEntityChange(d)}
        value={entityId}
        showHeader={false} 
      />
    )
  });

  const renderDeviceType = () => prepareFormItem({
      key: 'type',
      name: 'Device Type',
      element: <NodeTypeList />, 
  });

  const renderRanch = () => prepareFormItem({
    key: 'ranch',
    name: 'Ranch',
    element: <RanchSelect />
  });

  const renderBlock = () => prepareFormItem({
    key: 'block',
    name: 'Block',
    element: <BlockSelect />
  });

  const renderButton = () => (
    <Form.Item>
      <Button 
        type="primary" 
        htmlType="submit" 
        className="apply-btn"  
        loading={loading} 
      >
        {t('Get Nodes')}
      </Button>
    </Form.Item>
  );

  // Update extraFilters to support Form Item
  const updatedExtraFilters = extraFilters?.map((obj: any) => ({
    [obj.key]: prepareFormItem(obj),
  }))?.reduce((a: object, obj: object) => ({ ...a, ...obj }), {});

  const renderConfig: any = {
    status: renderStatus(),
    entity: renderEntity(),
    ranch: renderRanch(),
    block: renderBlock(),
    deviceType: renderDeviceType(),
    apply: renderButton(),
    ...(updatedExtraFilters || {}), // append extra filters to config
  };

  const onFinish = (values: any) => {
    if (onApply) {
      onApply({ ...values, status: selectedStatus, entity: entityId });
    }
  };

  const updatedInitialValues = {
    entity: renderItems?.includes('entity') ? entityId : undefined,
    type: 'field_node',
  };

  // Calculating colSpan based on the render items
  const colSpan = Math.floor(24 / (renderItems?.length || 1)); 

  return (
    <div className='optional-filter-parent-container'>
      <Form
        layout="vertical"
        wrapperCol={{ style: { minWidth: '50px', marginRight: '50px' } }}
        name="filters"
        className="flex-space-between form-margin-top"
        onFinish={onFinish}
        onValuesChange={(changedValues, allValues) => {
          if (onChange) {
            onChange({ ...allValues, status: selectedStatus, entity: entityId });
          }
        }}
        initialValues={updatedInitialValues}
      >
        <Row gutter={16} className="form-width-100 form-height">
          {renderItems?.map((renderKey: string) => (
            <Col span={colSpan} style={{ minWidth: 180 }}>
              {renderConfig?.[renderKey]}
            </Col>
          ))}
        </Row>
      </Form>
    </div>
  );
};

export default OptionalFilters;
